import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  /* background-color: rgb(40, 47, 60); */
  background-color: #17163b;
  border-radius: 2px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: auto;
  font-family: 'Quicksand', sans-serif;
`;

const ChartTitleStyled = styled.h2`
  margin-bottom: 20px;
  font-size: 20px;
  color: white;
  font-weight: 400;
  text-align: left;
  width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 330px;
  max-width: 800px;
  max-height: 600px;
  position: relative;
  overflow: hidden;
`;

const Tooltip = styled.div`
  position: absolute;
  background: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 200px; /* Set a max-width for the tooltip */
`;

const PageAuthorityVsRankScatterPlot = ({ data }) => {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      console.error('No data provided or data is empty.');
      return;
    }

    const svg = d3.select(svgRef.current);
    const containerWidth = svgRef.current.parentNode.clientWidth;
    const containerHeight = svgRef.current.parentNode.clientHeight || 600;

    // Define dimensions
    const margin = { top: 20, right: 20, bottom: 50, left: 60 };
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    // Clear previous contents
    svg.selectAll('*').remove();

    // Create SVG group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Format and sort data, select top 10 by rank
    const top10Data = data
      .filter(d => d.rank !== "na" && !isNaN(+d.rank)) // Filter out 'na' ranks
      .map(d => ({
        pageAuthority: +d.pa,
        rank: +d.rank,
        url: d.url // Include URL
      }))
      .sort((a, b) => a.rank - b.rank) // Sort by rank in ascending order
      .slice(0, 10); // Take top 10 by rank

    // Define scales
    const xScale = d3.scaleLinear()
      .domain([0, d3.max(top10Data, d => d.pageAuthority) || 1])
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([d3.max(top10Data, d => d.rank) || 1, 1])
      .range([height, 0]);

    // Define axes
    const xAxis = d3.axisBottom(xScale).ticks(5).tickFormat(d => d.toString());
    const yAxis = d3.axisLeft(yScale).ticks(10);

    // Append grid lines for x-axis
    g.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(5)
        .tickSize(-height)
        .tickFormat('')
      )
      .selectAll('.tick line')
      .attr('stroke', 'gray')
      .attr('stroke-width', '1px');

    // Append grid lines for y-axis
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .ticks(10)
        .tickSize(-width)
        .tickFormat('')
      )
      .selectAll('.tick line')
      .attr('stroke', 'gray')
      .attr('stroke-width', '1px');

    // Append x-axis
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .append('text')
      .attr('x', width / 2)
      .attr('y', 40)
      .attr('fill', 'lightgray')
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .text('Page Authority')
      .style('font-family', 'Crimson Text')

      g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text') 
      .style('fill', 'lightgray')
      .style('font-family', 'Crimson Text')
      .style('font-size', '16px')

    // Append y-axis
    g.append('g')
      .call(yAxis)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -50)
      .attr('x', -height / 2)
      .attr('fill', 'lightgray')
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .text('Rank')
      .style('font-family', 'Crimson Text')
      .style('fill', 'lightgray')

      g.append('g')
      .call(yAxis)
      .selectAll('text') 
      .style('fill', 'lightgray')
      .style('font-family', 'Crimson Text')
      .style('font-size', '16px')

      g.selectAll('.domain') // Axis line
      .style('stroke', 'lightgray') // Change the axis line color

    // Add scatter plot points
    g.selectAll('circle')
      .data(top10Data)
      .enter().append('circle')
      .attr('cx', d => xScale(d.pageAuthority))
      .attr('cy', d => yScale(d.rank))
      .attr('r', 5)
      .attr('fill', 'dodgerblue')
      .on('mouseover', function(event, d) {
        const tooltip = d3.select(tooltipRef.current);
        const [mouseX, mouseY] = d3.pointer(event, svgRef.current);

        // Tooltip size
        const tooltipWidth = 200; // Adjust as needed
        const tooltipHeight = 60; // Adjust as needed

        // Calculate tooltip position
        let tooltipLeft = mouseX + 10;
        let tooltipTop = mouseY - tooltipHeight - 10;

        // Ensure tooltip is within container bounds
        if (tooltipLeft + tooltipWidth > containerWidth) {
          tooltipLeft = mouseX - tooltipWidth - 10;
        }
        if (tooltipTop < 0) {
          tooltipTop = mouseY + 10;
        }

        tooltip
          .style('opacity', 1)
          .html(`Page Authority: ${d.pageAuthority}<br/>Rank: ${d.rank}<br/>URL: ${new URL(d.url).hostname || 'N/A'}`)
          .style('left', `${Math.max(0, Math.min(tooltipLeft, containerWidth - tooltipWidth))}px`)
          .style('top', `${Math.max(0, Math.min(tooltipTop, containerHeight - tooltipHeight))}px`);
      })
      .on('mousemove', function(event) {
        const tooltip = d3.select(tooltipRef.current);
        const [mouseX, mouseY] = d3.pointer(event, svgRef.current);

        // Tooltip size
        const tooltipWidth = 200; // Adjust as needed
        const tooltipHeight = 60; // Adjust as needed

        // Calculate tooltip position
        let tooltipLeft = mouseX + 10;
        let tooltipTop = mouseY - tooltipHeight - 10;

        // Ensure tooltip is within container bounds
        if (tooltipLeft + tooltipWidth > containerWidth) {
          tooltipLeft = mouseX - tooltipWidth - 10;
        }
        if (tooltipTop < 0) {
          tooltipTop = mouseY + 10;
        }

        tooltip
          .style('left', `${Math.max(0, Math.min(tooltipLeft, containerWidth - tooltipWidth))}px`)
          .style('top', `${Math.max(0, Math.min(tooltipTop, containerHeight - tooltipHeight))}px`);
      })
      .on('mouseout', function() {
        d3.select(tooltipRef.current)
          .style('opacity', 0);
      });

  }, [data]);

  return (
    <Container>
      <ChartTitleStyled><ScatterPlotIcon /> Page Authority vs. Rank Scatter Plot</ChartTitleStyled>
      <ChartContainer>
        <svg ref={svgRef} width="100%" height="100%"></svg>
        <Tooltip ref={tooltipRef}></Tooltip>
      </ChartContainer>
    </Container>
  );
};

export default PageAuthorityVsRankScatterPlot;
